import React, { useMemo, useState } from "react";
import { buildLedgerGroups } from "./ledgerGrouping";

// The flow diagram below is great for tracing one booking's story in detail,
// but with several debts open at once it's hard to answer at a glance: "we
// had 3 outstanding debts — which ones actually got adjusted into a booking,
// and which are still sitting open?" This table answers exactly that, one
// row per debt, in plain accounting language.
const StatusPill = ({ outstanding, original }) => {
  if (outstanding <= 0) {
    return (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700">
        Closed
      </span>
    );
  }
  if (outstanding < original) {
    return (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
        Partially Settled
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">
      Open
    </span>
  );
};

const statusOf = (group) => {
  if (group.outstandingAmount <= 0) return "Closed";
  if (group.outstandingAmount < group.originalDebt) return "Partially Settled";
  return "Open";
};

const VendorLedgerSummaryTable = ({ entries }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const groups = useMemo(() => buildLedgerGroups(entries), [entries]);

  if (groups.length === 0) return null;

  const openCount = groups.filter((g) => g.outstandingAmount > 0).length;
  const closedCount = groups.length - openCount;
  const totalOutstanding = groups.reduce((s, g) => s + g.outstandingAmount, 0);

  const filteredGroups =
    statusFilter === "all"
      ? groups
      : groups.filter((g) => statusOf(g) === statusFilter);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Debt Summary — {groups.length} debt{groups.length !== 1 ? "s" : ""} raised,{" "}
          {openCount} still open, {closedCount} closed
        </p>
        <div className="flex items-center gap-3">
          <select
            className="border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 dark:text-gray-200"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses ({groups.length})</option>
            <option value="Open">Open ({groups.filter((g) => statusOf(g) === "Open").length})</option>
            <option value="Partially Settled">
              Partially Settled ({groups.filter((g) => statusOf(g) === "Partially Settled").length})
            </option>
            <option value="Closed">Closed ({groups.filter((g) => statusOf(g) === "Closed").length})</option>
          </select>
          <p className="text-sm whitespace-nowrap">
            <span className="text-gray-400">Total still outstanding:</span>{" "}
            <span className="font-semibold text-red-600">₹{totalOutstanding}</span>
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/40 text-gray-500 dark:text-gray-400 text-left">
              <th className="px-3 py-2 whitespace-nowrap">Debt Raised From</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2 whitespace-nowrap">Debt Amount</th>
              <th className="px-3 py-2">Adjusted Into (Settled Against)</th>
              <th className="px-3 py-2 whitespace-nowrap">Outstanding</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                  No debts match this filter.
                </td>
              </tr>
            )}
            {filteredGroups.map((group) => (
              <tr
                key={group.ref_no}
                className="border-t border-gray-100 dark:border-gray-700 align-top"
              >
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-semibold text-gray-700 dark:text-gray-200">
                    {group.ref_no}
                  </div>
                  {group.client_name && (
                    <div className="text-gray-400">{group.client_name}</div>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[220px]">
                  {group.origins.map((o, i) => (
                    <div key={i} className={i > 0 ? "mt-1 pt-1 border-t border-gray-100" : ""}>
                      {o.description}
                      {o.remark && <div className="text-gray-400">— {o.remark}</div>}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">
                  ₹{group.originalDebt}
                </td>
                <td className="px-3 py-2">
                  {group.settledAgainst.length === 0 ? (
                    <span className="text-gray-400">Not adjusted anywhere yet</span>
                  ) : (
                    group.settledAgainst.map((s, i) => (
                      <div
                        key={i}
                        className={i > 0 ? "mt-1 pt-1 border-t border-gray-100" : ""}
                      >
                        <span className="font-semibold text-green-700">
                          {s.ref_no || "—"}
                        </span>
                        {s.client_name && (
                          <span className="text-gray-400"> ({s.client_name})</span>
                        )}
                        <div className="text-gray-500">
                          ₹{s.amount} on {s.date}
                          {s.paymentType ? ` · ${s.paymentType}` : ""}
                        </div>
                      </div>
                    ))
                  )}
                </td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">
                  <span
                    className={
                      group.outstandingAmount > 0 ? "text-red-600" : "text-green-600"
                    }
                  >
                    ₹{group.outstandingAmount}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <StatusPill
                    outstanding={group.outstandingAmount}
                    original={group.originalDebt}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorLedgerSummaryTable;
